import { FormattedMessage } from 'react-intl';
import {
  formatCurrency,
  handleOrderInformation,
  sumWithDeliveryFee,
  useTransmitData,
} from '@/service';
import { concat, each, map } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useInteractionState, useTranslation } from '@/hooks';
import { Cart, Order } from '@/type';
import { v4 as uuidv4 } from 'uuid';

type ShopCartConfirmationModalProps = {
  reference: string;
};

export const ShopCartConfirmationModal = ({
  reference,
}: ShopCartConfirmationModalProps) => {
  const { translation } = useInteractionState();
  const { language } = useTranslation();

  const { orders, setOrders, customer, setCustomer, management } =
    useInteractionState();
  const { mutateAsync, isSuccess, isError, isPending } = useTransmitData();

  const [isEmailRequired, setIsEmailRequired] = useState<boolean>(false);
  const [isSendOrderButtonDisable, setIsSendOrderButtonDisable] =
    useState<boolean>(false);

  const { price, deliveryFee, selfTaken } = sumWithDeliveryFee(
    orders,
    customer?.postal
  );

  useEffect(() => {
    setIsSendOrderButtonDisable(() => isEmailRequired && !customer?.email);
  }, [customer?.email, isEmailRequired]);

  const requireEmailConfirmation = (event: {
    target: { checked: boolean };
  }) => {
    setIsEmailRequired(event.target.checked);
  };

  const sendOrder = () => {
    if (!customer) {
      return;
    }

    setIsSendOrderButtonDisable(true);
    const { price, deliveryFee, selfTaken } = sumWithDeliveryFee(
      orders,
      customer.postal
    );
    const total = price + (deliveryFee ?? 0);
    each(orders, (it: Order) => (it.total = it.price * it.quantity));

    const deliveryFeeContact = selfTaken
      ? translation.formatMessage({ id: 'app.cart.menu.selfTaken' })
      : !selfTaken && !deliveryFee
      ? translation.formatMessage(
          { id: 'app.cart.menu.deliveryFeeContact' },
          { phone: management?.company?.telephone }
        )
      : undefined;

    const cart: Cart = {
      id: uuidv4(),
      reference: reference,
      customer: customer,
      price: price,
      deliveryFee: deliveryFee,
      deliveryFeeContact: deliveryFeeContact,
      total: total,
      isEmailRequired: isEmailRequired,
      orders: orders ?? [],
    };
    mutateAsync({
      path: '/api/cart',
      payload: cart,
    }).finally(() => {
      setIsSendOrderButtonDisable(false);
    });
  };

  const orderModal = map(orders, (it: Order, key: number) => {
    const { name } = handleOrderInformation(it, language);
    return (
      <tr key={key}>
        <td>{name}</td>
        <td>{it.price}</td>
        <td>{it.quantity}</td>
        <td>{formatCurrency(it.price * it.quantity, language)}</td>
      </tr>
    );
  });

  return (
    <div
      className="modal fade"
      id="orderConfirm"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="exampleModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title" id="exampleModalLabel">
              <FormattedMessage id="app.cart.confirm.title" />
            </h3>
            <h5>id-No.:{reference}</h5>
            <h4>{customer?.name}</h4>
            <h5>{customer?.email}</h5>
          </div>
          <div className="modal-body">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage
                      id="app.cart.menu.name"
                      defaultMessage="Name"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="app.cart.menu.price"
                      defaultMessage="Price"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="app.cart.menu.quantity"
                      defaultMessage="Quantity"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="app.cart.menu.total"
                      defaultMessage="Total"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderModal}
                <tr>
                  <td colSpan={4}>
                    <span className="right">
                      <FormattedMessage
                        id="app.cart.menu.total"
                        defaultMessage="Total"
                      />
                      &nbsp;:&nbsp;
                      {formatCurrency(price, language)}
                    </span>
                  </td>
                </tr>
                {!!deliveryFee && (
                  <tr>
                    <td colSpan={4}>
                      <span className="right">
                        <FormattedMessage
                          id="app.cart.menu.deliveryFee"
                          defaultMessage="Total"
                        />
                        &nbsp;:&nbsp;
                        {formatCurrency(deliveryFee, language)}
                      </span>
                    </td>
                  </tr>
                )}
                {deliveryFee === undefined && (
                  <tr>
                    <td colSpan={7}>
                      <span className="right list-group-item list-group-item-info">
                        <strong>
                          <FormattedMessage
                            id="app.cart.menu.deliveryFeeContact"
                            defaultMessage="Total"
                            values={{ phone: management?.company?.telephone }}
                          />
                        </strong>
                      </span>
                    </td>
                  </tr>
                )}
                {selfTaken && (
                  <tr>
                    <td colSpan={4}>
                      <span className="right">
                        <strong>
                          <FormattedMessage
                            id="app.cart.menu.selfTaken"
                            defaultMessage="Total"
                          />
                        </strong>
                      </span>
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={7}>
                    {!!deliveryFee && (
                      <span className="right">
                        <FormattedMessage
                          id="app.cart.menu.total.all"
                          defaultMessage="Total"
                        />
                        &nbsp;:&nbsp;
                        {formatCurrency(price + deliveryFee, language)}
                      </span>
                    )}
                    {!deliveryFee && (
                      <span className="right">
                        <FormattedMessage
                          id="app.cart.menu.total.all"
                          defaultMessage="Total"
                        />
                        &nbsp;:&nbsp;
                        {formatCurrency(price, language)}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            <p>
              <FormattedMessage
                id="app.contact.address"
                defaultMessage="address"
              />
              :&nbsp;
              {concat(
                customer?.address,
                ', ',
                customer?.postal,
                ', ',
                customer?.city
              )}
            </p>
            <p className="checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={isEmailRequired}
                  onChange={requireEmailConfirmation}
                />
                <FormattedMessage
                  id="app.cart.emailConfirm"
                  defaultMessage="emailConfirmation"
                />
              </label>
            </p>
            {isSendOrderButtonDisable && !isPending && !customer?.email && (
              <p className="alert alert-danger" role="alert">
                <FormattedMessage id="app.cart.emailMissing" />
              </p>
            )}
            {isSuccess && (
              <p className="alert alert-success" role="alert">
                <FormattedMessage id="app.cart.success" />
              </p>
            )}
            {isError && (
              <p className="alert alert-danger" role="alert">
                <FormattedMessage id="app.cart.error" />
              </p>
            )}
          </div>
          <div className="modal-footer">
            {!isSuccess && (
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
              >
                <FormattedMessage id="app.cart.cancel" />
              </button>
            )}
            {!isSuccess && (
              <button
                style={{
                  color: isSendOrderButtonDisable ? '#808080' : '',
                }}
                disabled={isSendOrderButtonDisable || isError || !customer}
                type="button"
                className="btn btn-primary"
                onClick={sendOrder}
              >
                <FormattedMessage id="app.cart.confirm" />
              </button>
            )}
            {isSuccess && (
              <button
                type="button"
                className="btn btn-primary"
                data-dismiss="modal"
                onClick={() => {
                  setOrders?.([]);
                  setCustomer?.(null);
                }}
              >
                <FormattedMessage id="app.contact.closeButton" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
